// ModernWorkspacePreference — Modern Layout の構造化ワークスペース state を per-user で
// localStorage に永続化する infrastructure 層ユーティリティ.
//
// 背景 (docs/_wip/modern-layout.md §9.10、consult 2026-06-14 / 2026-06-16):
//   - §9.10 決定2: B (preset/ミラー) も C (手動編集) も **同一の1本のレイアウト state**
//     を操作する。`layoutPreference` (= Classic/Modern 種別) とは別軸 (= Modern UI 内の
//     配置) なので **キーを分離** する (§9.10 決定3「軸を混ぜない」)。
//   - 第1スライス (2026-06-14) は `{ mirror: boolean }` だけ永続化した。第2スライス
//     (2026-06-16、本ファイル) で **名前付き Tier1 preset** へ一般化し `{ presetId }` を
//     永続化する。後続スライス (C: 手動配置/幅/畳みの直接編集) では同じ 1 本の state に
//     項目が増える前提で、最初から JSON オブジェクトで保存する (§9 事前準備パターン)。
//   - **後方互換**: 旧 `{ mirror: true }` を読めること (= 第1スライスのユーザーが第2スライス
//     後も「左利き」配置のまま継続)。`mirror:true` → preset `'mirror'`、`mirror:false` /
//     不在 → preset `'standard'` にマッピングする (= 前方互換の逆: 旧フィールドの引き上げ)。
//   - `layout` (Classic/Modern) と混同しないよう **別キー** `oekaki:modern-workspace`。
//   - S2 (floating-panels-2026-08-01.md §6 決定4、2026-08-01): `WorkspaceState.floating`
//     (フロート配置) を同じ 1 本の `{workspace}` 側にそのまま乗せる (= 新永続キーを作らない)。
//     後方互換: floating を知らない旧 client の `parseWorkspace` は不変条件チェック
//     (全パネルが panels 配列に揃っている前提) で弾かれ default (standard) に倒れる
//     (= 壊れない、最悪ドック配置に戻るだけ)。前方互換: floating フィールド無しの旧 JSON も
//     そのまま読める (= 省略 = 全パネル docked)。
//   - S2 §11 (floating-panels-2026-08-01.md、consult 2026-08-01): 「⇤ドック」の戻り先は
//     当初 state 保持案 (`floatOrigin`) を検討したが、**位置ベース + 方向ラベル**へ差し替えて
//     撤回した (= 現在のフロート座標から都度導出、`floatPanelSide`/`edgeSnapRegion`)。永続化
//     フォーマットへの追加は不要になった。
//   - **パネル migration** (modern-brush-panel-2026-08-17.md §6、2026-08-17):
//     `validateWorkspace` は全 PanelId が揃うことを不変条件にしているため、新しい PanelId が
//     増えると保存済み custom state は「missing panel」で不変条件違反になる。`parseWorkspace`
//     は validate に通す**前**に `repairMissingPanels` (workspaceLayout.ts) で欠けているパネル
//     を既定位置へ補うことで、この不変条件違反による default フォールバック (= 既存ユーザーの
//     手動配置が丸ごと消える事故) を避ける。

import {
  ALL_PANEL_IDS,
  DEFAULT_PRESET_ID,
  type FloatPosition,
  isWorkspacePresetId,
  type PanelId,
  repairMissingPanels,
  validateWorkspace,
  type WorkspacePresetId,
  type WorkspaceRegion,
  type WorkspaceState,
} from '../presentation/layouts/modern/workspaceLayout.js';

/** localStorage キー: Modern ワークスペース state (= 配置)。layout 種別とは別軸・別キー. */
export const MODERN_WORKSPACE_KEY = 'oekaki:modern-workspace';

/**
 * 永続化される Modern ワークスペース state (= 構造化レイアウト state の serializable 版).
 *
 * 第3スライス (consult 2026-06-14 決定2「手動編集 = 直接編集」) で **discriminated union**
 * へ拡張:
 *   - クリーンな preset 選択中 → `{ presetId }` (= 第2スライスと同一・最小)。
 *   - 手動編集済み「カスタム」→ `{ workspace }` (= full WorkspaceState を保存)。
 *
 * **後方互換**: 旧 `{ presetId }` / `{ mirror:boolean }` も読める (= normalize で吸収)。
 * **前方互換**: 古い client が新 `{ workspace }` を読むと presetId 不在 → standard に倒れる
 * (= 壊れない。最悪レイアウトが既定に戻るだけ)。
 */
export type ModernWorkspacePref =
  /** クリーンな Tier1 preset 選択 (= 標準 / 左利き / お絵描き集中 / …). */
  | { readonly presetId: WorkspacePresetId }
  /** 手動編集済み無名カスタム配置 (= full 構造化 state、§9.10 第3スライス). */
  | { readonly workspace: WorkspaceState };

/** 既定 = 「標準」preset (= 現状配置)。 */
export const DEFAULT_MODERN_WORKSPACE: ModernWorkspacePref = {
  presetId: DEFAULT_PRESET_ID,
};

/**
 * Minimal Storage shape this module uses (layoutPreference.ts 等と同流儀).
 * `globalThis.localStorage` matches; tests pass a mock with the same surface.
 */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface ModernWorkspacePreferenceStore {
  /** 現在の state を読み出し. 不在 / 不正 / storage 不可 → `DEFAULT_MODERN_WORKSPACE`. */
  get(): ModernWorkspacePref;
  /** state を保存 (JSON). storage 不可なら no-op (例外を throw しない). */
  set(pref: ModernWorkspacePref): void;
  /** key を削除. テスト / 設定リセット用. storage 不可なら no-op. */
  clear(): void;
}

/**
 * `globalThis.localStorage` をプローブして使用可能なら返す, さもなくば `null`.
 * layoutPreference.ts の probe を流用 (sentinel write + readback で Safari private
 * browsing / QuotaExceeded を弾く).
 */
const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:modern-workspace__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

export interface CreateModernWorkspacePreferenceStoreOptions {
  /**
   * Override the underlying storage. Default: probe `globalThis.localStorage`,
   * fall through to no-op if unavailable. Pass `null` to **force** the no-op path
   * (= getter always returns DEFAULT_MODERN_WORKSPACE, setter silently drops).
   *
   * テストでは小さな `Map` ベースの fake を渡す.
   */
  storage?: StorageLike | null;
}

/** 全 WorkspaceRegion (= parse 時の network of regions の網羅チェックに使う安定列)。 */
const ALL_REGIONS: ReadonlyArray<WorkspaceRegion> = [
  'leftRail',
  'toolColumn',
  'canvas',
  'rightDock',
];

/** 任意値が PanelId 配列か (= 全要素が既知 PanelId)。 */
const isPanelIdArray = (v: unknown): v is PanelId[] =>
  Array.isArray(v) && v.every((x) => ALL_PANEL_IDS.includes(x as PanelId));

/**
 * 保存 JSON を `WorkspaceState` へ厳格に parse する (= custom 配置の復元).
 * 構造 (order / panels / 任意の hidden / 任意の widths) を型と値域で検証し、最後に
 * `validateWorkspace` で不変条件 (全パネル健在・canvas 単独・canvas 非 hidden) を確認する.
 * 1 つでも崩れていれば `null` (= 呼び出し側が default に倒す。壊れた custom で UI を壊さない).
 */
const parseWorkspace = (raw: unknown): WorkspaceState | null => {
  if (typeof raw !== 'object' || raw === null) return null;
  const obj = raw as Record<string, unknown>;
  // order: 既知 region のみの配列。
  if (
    !Array.isArray(obj.order) ||
    !obj.order.every((r) => ALL_REGIONS.includes(r as WorkspaceRegion))
  ) {
    return null;
  }
  const order = obj.order as WorkspaceRegion[];
  // panels: 全 region について PanelId 配列。
  if (typeof obj.panels !== 'object' || obj.panels === null) return null;
  const panelsObj = obj.panels as Record<string, unknown>;
  const panels = {} as Record<WorkspaceRegion, ReadonlyArray<PanelId>>;
  for (const region of ALL_REGIONS) {
    const arr = panelsObj[region];
    if (!isPanelIdArray(arr)) return null;
    panels[region] = arr;
  }
  // hidden (任意): PanelId → boolean の部分マップ。未知 key / 非 boolean は捨てる。
  let hidden: Partial<Record<PanelId, boolean>> | undefined;
  if (typeof obj.hidden === 'object' && obj.hidden !== null) {
    const h = obj.hidden as Record<string, unknown>;
    const acc: Partial<Record<PanelId, boolean>> = {};
    for (const id of ALL_PANEL_IDS) {
      if (typeof h[id] === 'boolean') acc[id] = h[id] as boolean;
    }
    if (Object.keys(acc).length > 0) hidden = acc;
  }
  // widths (任意): region → number。非有限は捨てる (clamp は描画時に asideWidthPx が掛ける)。
  let widths: Partial<Record<WorkspaceRegion, number>> | undefined;
  if (typeof obj.widths === 'object' && obj.widths !== null) {
    const w = obj.widths as Record<string, unknown>;
    const acc: Partial<Record<WorkspaceRegion, number>> = {};
    for (const region of ALL_REGIONS) {
      const n = w[region];
      if (typeof n === 'number' && Number.isFinite(n)) acc[region] = n;
    }
    if (Object.keys(acc).length > 0) widths = acc;
  }
  // floating (任意、S2 floating-panels-2026-08-01.md §6): PanelId → {x,y} の部分マップ。
  // 未知 key / 非有限座標は捨てる (壊れたエントリだけ落とし、他は活かす)。canvas / rail パネル
  // の混入や座標の有限性は最終的に validateWorkspace が不変条件として弾く (= 二重防御)。
  let floating: Partial<Record<PanelId, FloatPosition>> | undefined;
  if (typeof obj.floating === 'object' && obj.floating !== null) {
    const f = obj.floating as Record<string, unknown>;
    const acc: Partial<Record<PanelId, FloatPosition>> = {};
    for (const id of ALL_PANEL_IDS) {
      const v = f[id];
      if (typeof v !== 'object' || v === null) continue;
      const { x, y } = v as Record<string, unknown>;
      if (
        typeof x === 'number' &&
        Number.isFinite(x) &&
        typeof y === 'number' &&
        Number.isFinite(y)
      ) {
        acc[id] = { x, y };
      }
    }
    if (Object.keys(acc).length > 0) floating = acc;
  }
  const state: WorkspaceState = {
    order,
    panels,
    ...(hidden ? { hidden } : {}),
    ...(widths ? { widths } : {}),
    ...(floating ? { floating } : {}),
  };
  // migration (modern-brush-panel-2026-08-17.md §6): この JSON が保存された後に新しい
  // PanelId が増えていた場合、この state には未知の (= 保存時点で存在しなかった) パネルが
  // 欠けている。それを「壊れた state」として捨てる前に、欠けているパネルだけ既定位置へ
  // 補ってから不変条件チェックへ渡す (= 手動配置を活かしたまま migrate)。既に全パネルが
  // 揃っている state は repairMissingPanels が no-op で同一参照を返すので、ここでの挙動は
  // 変わらない。
  const repaired = repairMissingPanels(state);
  // 不変条件 (全パネル健在・canvas 単独・canvas 非 hidden 等) を満たさなければ捨てる。
  // repairMissingPanels は「欠け」しか直さないので、型/構造レベルで本当に壊れている
  // state (重複・canvas 侵犯等) はここで引き続き弾かれる。
  return validateWorkspace(repaired) === null ? repaired : null;
};

/**
 * 保存 JSON を `ModernWorkspacePref` へ正規化する. 既知フィールドだけ読み、欠落 /
 * 型不一致は default にフォールバック (= 前方互換: 未知フィールドは無視).
 *
 * 読み取り優先順位:
 *   1. 新 `presetId` (= クリーンな preset 選択)。
 *   2. 第3スライス `{ workspace }` (= 手動編集済み custom、不変条件を満たすときのみ)。
 *   3. 後方互換 `{ mirror:boolean }` を preset へ引き上げ (`true→'mirror'` / `false→'standard'`)。
 *   4. いずれも無ければ default (standard)。
 * presetId と workspace が両方ある異常データは presetId を優先 (= 前方の正・clean を採る)。
 */
const normalize = (raw: unknown): ModernWorkspacePref => {
  if (typeof raw !== 'object' || raw === null) return DEFAULT_MODERN_WORKSPACE;
  const obj = raw as Record<string, unknown>;
  // 1. 新スキーマ: presetId が有効ならそれを採用 (clean 優先)。
  if (isWorkspacePresetId(obj.presetId)) {
    return { presetId: obj.presetId };
  }
  // 2. 第3スライス: custom full state。不変条件を満たすときのみ採用。
  if ('workspace' in obj) {
    const ws = parseWorkspace(obj.workspace);
    if (ws !== null) return { workspace: ws };
  }
  // 3. 後方互換: 旧 { mirror: boolean } を preset へ引き上げ。
  if (typeof obj.mirror === 'boolean') {
    return { presetId: obj.mirror ? 'mirror' : 'standard' };
  }
  return DEFAULT_MODERN_WORKSPACE;
};

const readPref = (storage: StorageLike): ModernWorkspacePref => {
  try {
    const raw = storage.getItem(MODERN_WORKSPACE_KEY);
    if (raw === null) return DEFAULT_MODERN_WORKSPACE;
    return normalize(JSON.parse(raw));
  } catch {
    // JSON parse 失敗 / SecurityError 等 — default に倒す (例外で UI を壊さない).
    return DEFAULT_MODERN_WORKSPACE;
  }
};

const writePref = (storage: StorageLike, pref: ModernWorkspacePref): void => {
  try {
    storage.setItem(MODERN_WORKSPACE_KEY, JSON.stringify(pref));
  } catch {
    // QuotaExceeded / SecurityError 等 — 永続化できないだけで動作は継続させる.
  }
};

/**
 * `ModernWorkspacePreferenceStore` のインスタンスを構築. storage が unavailable な
 * 環境 (SSR / Safari private browsing 厳格モード) でも getter は default を返すだけで、
 * setter は no-op (= 例外で UI が壊れることはない).
 */
export const createModernWorkspacePreferenceStore = (
  opts: CreateModernWorkspacePreferenceStoreOptions = {},
): ModernWorkspacePreferenceStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());

  return {
    get() {
      if (storage === null) return DEFAULT_MODERN_WORKSPACE;
      return readPref(storage);
    },
    set(pref) {
      if (storage === null) return;
      writePref(storage, pref);
    },
    clear() {
      if (storage === null) return;
      try {
        storage.removeItem(MODERN_WORKSPACE_KEY);
      } catch {
        // ignore
      }
    },
  };
};
